'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BookOpen, CalendarDays, GraduationCap, Mail, MapPin, School, ShieldCheck, X } from 'lucide-react';
import { birthYearOptions, gradeOptions, stateOptions } from '@/constant/value';
import SimpleSelect from '@/components/ui/SimpleSelect';
import Select from '@/components/ui/Select';
import { createPortal } from 'react-dom';
import { getCurrentTier } from '@/store/useSubscriptionStore';
import SubjectPaywallModal from '@/components/Subscription/SubjectPaywallModal';
import { apiClient } from '@/lib/api-client';

const USERNAME_PASSWORD_MIN_LENGTH = 6;
const PRIMARY_BUTTON_CLASS = 'display-text-semi inline-flex items-center justify-center rounded-xl bg-[#42C62C] py-2.5 text-sm text-white transition-colors hover:bg-[#38A824] disabled:cursor-not-allowed disabled:opacity-70';
const SECONDARY_BUTTON_CLASS = 'display-text-semi inline-flex items-center justify-center rounded-xl bg-[#ECEEF2] py-2.5 text-sm text-[#2F3A4A] transition-colors hover:bg-[#E2E6EC] disabled:cursor-not-allowed disabled:opacity-70';

const SUBJECT_OPTIONS = [
  { value: 'math', label: 'Math' },
  { value: 'science', label: 'Science' },
  { value: 'history', label: 'Social Studies' },
  { value: 'vocabulary', label: 'Vocabulary' },
  { value: 'grammar', label: 'Grammar' },
  { value: 'amc-math', label: 'AMC Math' },
];

function ParentSubjectSection({
  freeSubject,
}: {
  freeSubject: string | null;
}) {
  const [showPaywall, setShowPaywall] = useState(false);
  const subjectLabel = SUBJECT_OPTIONS.find((o) => o.value === freeSubject)?.label ?? freeSubject ?? 'Not set';

  return (
    <>
      <div className="w-full rounded-[20px] border border-[#FFE7B8] bg-[#FFF9EC] p-2.5">
        <div className="flex items-center justify-center gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-sm">
              <BookOpen className="h-4 w-4 text-[#E59200]" />
            </div>
            <div>
              <p className="display-text text-[10px] font-black uppercase tracking-[0.17em] text-[#D39214]">Current Subject</p>
              <p className="display-text-bold text-[1.35rem] leading-none text-[#1A1C1E]">{subjectLabel}</p>
            </div>
          </div>
          {freeSubject && (
            <button
              type="button"
              onClick={() => setShowPaywall(true)}
              className="display-text-semi rounded-full bg-[#FFB800] px-4 py-1.5 text-[12px] text-white shadow-[0_10px_24px_rgba(255,184,0,0.28)] transition-transform hover:scale-[1.02]"
            >
              Change
            </button>
          )}
        </div>
      </div>
      {freeSubject && (
        <SubjectPaywallModal
          visible={showPaywall}
          blockedSubject={freeSubject}
          onClose={() => setShowPaywall(false)}
        />
      )}
    </>
  );
}

function SubjectSection({
  teacherClassName,
  onJoinClass,
  relationalAction,
}: {
  teacherClassName?: string | null;
  onJoinClass?: () => void;
  relationalAction?: React.ReactNode;
}) {
  const tier = getCurrentTier();

  if (tier !== 'free') return null;

  const classLabel = teacherClassName ? `${teacherClassName}'s class` : 'Not joined';

  return (
    <div className="w-full max-w-[236px]">
      <div className="flex w-full flex-col items-stretch gap-2">
        <div className="w-full rounded-[16px] bg-white px-3 py-2.5 text-center shadow-[0_10px_24px_rgba(101,65,196,0.08)]">
          <div className="min-w-0 text-center">
            <p className="display-text text-[10px] font-black uppercase tracking-[0.22em] text-[#7C8092]">Class</p>
            <p className="display-text-semi mt-1 text-[12px] leading-snug text-[#1A1C1E] break-words">{classLabel}</p>
          </div>
          {onJoinClass ? (
            <button
              type="button"
              onClick={() => onJoinClass()}
              className="display-text-semi mt-2 inline-flex h-[28px] w-full items-center justify-center rounded-full bg-[#6541C4] px-3 text-[11px] text-white shadow-[0_8px_18px_rgba(101,65,196,0.18)] transition-colors hover:bg-[#5735B5]"
            >
              {teacherClassName ? 'Change Class' : 'Join Class'}
            </button>
          ) : null}
        </div>

        {relationalAction ? (
          <div className="flex justify-start">{relationalAction}</div>
        ) : null}
      </div>
    </div>
  );
}

type SelectOption = { value: string; label: string };

// ─── Student-mode data (from own auth profile) ───────────────────────────────

export interface StudentProfileData {
  name: string;
  loginIdentifier: string; // email or username, always read-only
  birthYear: string;
  grade: string;
  state: SelectOption | null;
  school: { school_name: string; city?: string; state?: string } | null;
  freeSubject?: string | null;
}

// ─── Parent-mode data (fetched by parent on behalf of student) ────────────────

export interface ParentStudentProfileData {
  studentId: string;
  name: string;
  loginIdentifier: string;
  birthYear: string;
  grade: string;
  state: SelectOption | null;
  school: { school_name: string; city?: string; state?: string } | null;
  freeSubject?: string | null;
  subjectChangedAt?: string | null;
}

// ─── Save callbacks ───────────────────────────────────────────────────────────

export interface StudentProfileSaveData {
  name: string;
  birthYear: string;
  grade: string;
  state: SelectOption | null;
  school: { school_name: string; city?: string; state?: string } | null;
  /** Only present when the user typed a new password */
  newPassword?: string;
}

export interface ParentStudentProfileSaveData extends StudentProfileSaveData {
  studentId: string;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface BaseProps {
  onClose: () => void;
  /** Extra UI shown below the login identifier (student: logout links, parent: nothing) */
  footerLeadingAction?: React.ReactNode;
  footerTrailingAction?: React.ReactNode;
  roleLabel?: string | null;
  teacherClassName?: string | null;
  hideClassAction?: boolean;
}

interface StudentModeProps extends BaseProps {
  mode: 'student';
  profileData: StudentProfileData;
  onSave: (data: StudentProfileSaveData) => Promise<void>;
  onManageSubject?: () => void;
  onJoinClass?: () => void;
}

interface ParentModeProps extends BaseProps {
  mode: 'parent';
  profileData: ParentStudentProfileData;
  onSave: (data: ParentStudentProfileSaveData) => Promise<void>;
}

export type StudentProfileModalProps = StudentModeProps | ParentModeProps;

// ─── Component ────────────────────────────────────────────────────────────────

export default function StudentProfileModal(props: StudentProfileModalProps) {
  const { onClose, footerLeadingAction, footerTrailingAction, roleLabel = null, teacherClassName = null, hideClassAction = false } = props;

  const [isEditing, setIsEditing] = useState(false);

  // Edit fields
  const [name, setName] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [grade, setGrade] = useState('');
  const [state, setState] = useState<SelectOption | null>(null);
  const [school, setSchool] = useState('');
  const [selectedSchool, setSelectedSchool] = useState<any>(null);
  const [schoolSuggestions, setSchoolSuggestions] = useState<any[]>([]);
  const [showSchoolSuggestions, setShowSchoolSuggestions] = useState(false);
  const [isLoadingSchools, setIsLoadingSchools] = useState(false);
  const schoolInputRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Password – always show the row in both modes.
  // In view mode: display a fixed masked placeholder.
  // In edit mode: blank = keep current, non-blank = change password.
  const [password, setPassword] = useState('');

  // Feedback
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const pd = props.profileData;

  const handleStartEdit = () => {
    setName(pd.name);
    setBirthYear(pd.birthYear);
    setGrade(pd.grade);
    setState(pd.state);
    setSchool(pd.school?.school_name ?? '');
    setSelectedSchool(pd.school ?? null);
    setSchoolSuggestions([]);
    setShowSchoolSuggestions(false);
    setSaveError('');
    setFieldErrors({});
    setIsFormSubmitted(false);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setSaveError('');
    setFieldErrors({});
    setIsFormSubmitted(false);
    setName('');
    setPassword('');
  };

  // ── School search ──────────────────────────────────────────────────────────

  const performSearch = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setSchoolSuggestions([]);
      return;
    }
    setIsLoadingSchools(true);
    try {
      const stateParam = state?.value ? `&state_id=${encodeURIComponent(state.value)}` : '';
      const res = await apiClient.get(`/api/schools?q=${encodeURIComponent(query)}&limit=30${stateParam}`);
      if (res.ok) {
        const data = await res.json();
        setSchoolSuggestions(Array.isArray(data) ? data : []);
      } else {
        setSchoolSuggestions([]);
      }
    } catch {
      setSchoolSuggestions([]);
    } finally {
      setIsLoadingSchools(false);
    }
  }, [state]);

  const debouncedSearch = useCallback((query: string) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => performSearch(query), 300);
  }, [performSearch]);

  useEffect(() => () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (schoolInputRef.current && !schoolInputRef.current.contains(e.target as Node)) {
        setShowSchoolSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSchoolChange = (value: string) => {
    setSchool(value);
    setSelectedSchool(null);
    debouncedSearch(value);
    setShowSchoolSuggestions(true);
  };

  const handleSchoolSelect = (s: any) => {
    setSchool(s.school_name);
    setSelectedSchool(s);
    setShowSchoolSuggestions(false);
    setSchoolSuggestions([]);
  };

  // ── Validation ─────────────────────────────────────────────────────────────

  const validate = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = 'Name is required';
    if (!birthYear) errors.birthYear = 'Birth year is required';
    if (!grade) errors.grade = 'Grade is required';
    if (!state?.value) errors.state = 'State is required';
    if (!school.trim()) errors.school = 'School is required';
    const pw = password.trim();
    if (pw && pw.length < USERNAME_PASSWORD_MIN_LENGTH) {
      errors.password = `Password must be at least ${USERNAME_PASSWORD_MIN_LENGTH} characters`;
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [name, birthYear, grade, state, school, password]);

  useEffect(() => {
    if (isFormSubmitted) validate();
  }, [name, birthYear, grade, state, school, password, isFormSubmitted, validate]);

  const isFormValid = useMemo(
    () => {
      if (!name.trim() || !birthYear || !grade || !state?.value || !school.trim()) return false;
      const pw = password.trim();
      if (pw && pw.length < USERNAME_PASSWORD_MIN_LENGTH) return false;
      return true;
    },
    [name, birthYear, grade, state, school, password],
  );

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setIsFormSubmitted(true);
    if (!validate()) return;

    setIsSaving(true);
    setSaveError('');
    try {
      const pw = password.trim();
      const saveData: StudentProfileSaveData = {
        name: name.trim(),
        birthYear,
        grade,
        state,
        school: selectedSchool || (school.trim() ? { school_name: school.trim() } : null),
        ...(pw ? { newPassword: pw } : {}),
      };

      if (props.mode === 'parent') {
        await (props as ParentModeProps).onSave({
          ...(saveData as StudentProfileSaveData),
          studentId: (props as ParentModeProps).profileData.studentId,
        } as ParentStudentProfileSaveData);
      } else {
        await (props as StudentModeProps).onSave(saveData);
      }

      setIsEditing(false);
    } catch (err: any) {
      setSaveError(err?.message || 'Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Derived display values ─────────────────────────────────────────────────

  const displayBirthYear = pd.birthYear || '';
  const displayGrade = pd.grade || '';
  const displayState = pd.state?.value || '';
  const displaySchool = pd.school?.school_name || '';
  const nameInitial = pd.name ? pd.name.charAt(0).toUpperCase() : '?';

  // ── Render ─────────────────────────────────────────────────────────────────

  if (typeof window === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(17, 24, 39, 0.45)', zIndex: 200 }}
      onClick={() => { if (!isSaving) onClose(); }}
    >
      <div
        className="flex w-full max-w-[744px] max-h-[88vh] flex-col overflow-hidden rounded-[22px] border border-white/50 bg-white shadow-[0_18px_56px_rgba(101,65,196,0.11)] animate-fade-in md:flex-row"
        style={{ fontFamily: 'Rubik, sans-serif' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex w-full flex-col border-b border-slate-100 bg-[linear-gradient(180deg,#FBFAFF_0%,#F5F1FF_100%)] px-4 py-3 md:w-[29%] md:border-b-0 md:border-r md:px-5 md:py-5">
          <div className="flex w-full items-center gap-3 md:flex-col md:items-center md:justify-start md:pt-2 md:text-center">
            <div className="relative shrink-0 md:mb-2.5">
              <div className="flex h-[52px] w-[52px] items-center justify-center rounded-full bg-[#6541C4] text-[1.18rem] font-extrabold text-white shadow-[0_14px_34px_rgba(101,65,196,0.22)] ring-[3.5px] ring-white md:h-[65px] md:w-[65px] md:text-[1.48rem] md:ring-4">
                {nameInitial}
              </div>
              <div className="absolute bottom-0.5 right-0.5 h-[14px] w-[14px] rounded-full border-[2.5px] border-[#F8F9FE] bg-[#22C55E] md:h-[17px] md:w-[17px]" />
            </div>

            <div className="min-w-0 flex-1 md:flex-none">
              {isEditing ? (
                <div>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter name"
                    className="display-text-bold w-full bg-transparent text-[1.06rem] leading-tight text-[#1A1C1E] outline-none border-b border-[#D8DDE7] focus:border-[#6541C4] pb-0.5 md:text-[1.4rem]"
                  />
                  {fieldErrors.name && (
                    <p className="display-text pt-1 text-xs text-red-500">{fieldErrors.name}</p>
                  )}
                </div>
              ) : (
                <h2 className="display-text-bold text-[1.06rem] leading-tight text-[#1A1C1E] md:text-[1.4rem]">{pd.name || ''}</h2>
              )}
            </div>
          </div>

          {props.mode === 'student' ? (
            <div className="mt-4 flex w-full justify-center md:mt-5">
              <SubjectSection
                teacherClassName={teacherClassName}
                onJoinClass={hideClassAction ? undefined : (props as StudentModeProps).onJoinClass}
                relationalAction={footerLeadingAction}
              />
            </div>
          ) : null}
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="relative flex items-center justify-between px-5 pb-2 pt-4.5 md:px-6 md:pb-2.5 md:pt-5">
            <h3 className="display-text-bold absolute left-1/2 top-4.5 -translate-x-1/2 text-[1rem] uppercase tracking-[0.18em] text-[#1A1C1E] md:top-5 md:text-[1.04rem] md:tracking-[0.2em]">
              {props.mode === 'parent' ? "Student's Profile" : 'My Profile'}
            </h3>
            <button
              onClick={() => { if (!isSaving) onClose(); }}
              className="ml-auto flex h-8 w-8 items-center justify-center rounded-full text-[#74777F] transition-colors hover:bg-slate-100"
            >
              <X size={16} />
            </button>
          </div>

          <div className="custom-scrollbar flex-1 overflow-y-auto px-5 pb-4 md:px-6 md:pb-4.5">
            <div className="grid grid-cols-1 gap-x-7 gap-y-3 sm:grid-cols-2 md:gap-y-3.5">
              <div className="space-y-1">
                <label className="display-text text-[10px] font-black uppercase tracking-[0.18em] text-[#74777F]/70">Username</label>
                <div className="flex items-start gap-2">
                  <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#A0A4AE]" />
                  <p className="display-text-bold break-all text-[14px] md:text-[15px] text-[#1A1C1E]">{pd.loginIdentifier || ''}</p>
                </div>
              </div>

              <div className="space-y-1">
                <label className="display-text text-[10px] font-black uppercase tracking-[0.18em] text-[#74777F]/70">Password</label>
                {isEditing ? (
                  <div>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Unchanged if blank"
                      autoComplete="new-password"
                      className="display-text h-10 w-full rounded-[14px] border border-[#D8DDE7] bg-white px-3.5 text-sm text-[#1A1C1E] outline-none transition-colors focus:border-[#6541C4]"
                    />
                    {fieldErrors.password && (
                      <p className="display-text pt-1 text-xs text-red-500">{fieldErrors.password}</p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#A0A4AE]" />
                    <p className="display-text-bold text-[14px] md:text-[15px] tracking-[0.22em] text-[#1A1C1E]">••••••••</p>
                  </div>
                )}
              </div>

              {(isEditing || displayBirthYear) && (
              <div className="space-y-1">
                <label className="display-text text-[10px] font-black uppercase tracking-[0.18em] text-[#74777F]/70">Birth Year</label>
                {isEditing ? (
                  <SimpleSelect
                    value={birthYear}
                    onChange={setBirthYear}
                    options={birthYearOptions()}
                    placeholder="Select birth year"
                    isSearchable
                    isClearable={false}
                    error={fieldErrors.birthYear}
                    styles={{
                      control: (base: any) => ({ ...base, minHeight: '40px', height: '40px', borderRadius: '14px', borderColor: '#D8DDE7', boxShadow: 'none', '&:hover': { borderColor: '#6541C4' } }),
                      valueContainer: (base: any) => ({ ...base, height: '40px', padding: '0 10px' }),
                      indicatorsContainer: (base: any) => ({ ...base, height: '40px' }),
                      singleValue: (base: any) => ({ ...base, fontSize: '0.9rem', fontFamily: 'var(--font-display)', fontWeight: 500 }),
                      placeholder: (base: any) => ({ ...base, fontSize: '0.9rem', fontFamily: 'var(--font-display)' }),
                      input: (base: any) => ({ ...base, fontFamily: 'var(--font-display)' }),
                    }}
                  />
                ) : (
                  <div className="flex items-start gap-2">
                    <CalendarDays className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#A0A4AE]" />
                    <p className="display-text-bold text-[14px] md:text-[15px] text-[#1A1C1E]">{displayBirthYear}</p>
                  </div>
                )}
              </div>
              )}

              {(isEditing || displayGrade) && (
              <div className="space-y-1">
                <label className="display-text text-[10px] font-black uppercase tracking-[0.18em] text-[#74777F]/70">Grade</label>
                {isEditing ? (
                  <SimpleSelect
                    value={grade}
                    onChange={setGrade}
                    options={gradeOptions()}
                    placeholder="Select grade"
                    isSearchable
                    isClearable={false}
                    error={fieldErrors.grade}
                    styles={{
                      control: (base: any) => ({ ...base, minHeight: '40px', height: '40px', borderRadius: '14px', borderColor: '#D8DDE7', boxShadow: 'none', '&:hover': { borderColor: '#6541C4' } }),
                      valueContainer: (base: any) => ({ ...base, height: '40px', padding: '0 10px' }),
                      indicatorsContainer: (base: any) => ({ ...base, height: '40px' }),
                      singleValue: (base: any) => ({ ...base, fontSize: '0.9rem', fontFamily: 'var(--font-display)', fontWeight: 500 }),
                      placeholder: (base: any) => ({ ...base, fontSize: '0.9rem', fontFamily: 'var(--font-display)' }),
                      input: (base: any) => ({ ...base, fontFamily: 'var(--font-display)' }),
                    }}
                  />
                ) : (
                  <div className="flex items-start gap-2">
                    <GraduationCap className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#A0A4AE]" />
                    <p className="display-text-bold text-[14px] md:text-[15px] text-[#1A1C1E]">{displayGrade}</p>
                  </div>
                )}
              </div>
              )}

              {(isEditing || displayState) && (
              <div className="space-y-1">
                <label className="display-text text-[10px] font-black uppercase tracking-[0.18em] text-[#74777F]/70">State</label>
                {isEditing ? (
                  <Select
                    value={state}
                    onChange={(v: any) => setState(v)}
                    options={stateOptions()}
                    placeholder="Select state"
                    isSearchable
                    isClearable={false}
                    error={fieldErrors.state}
                    styles={{
                      control: (base: any) => ({ ...base, minHeight: '40px', height: '40px', borderRadius: '14px', borderColor: '#D8DDE7', boxShadow: 'none', '&:hover': { borderColor: '#6541C4' } }),
                      valueContainer: (base: any) => ({ ...base, height: '40px', padding: '0 10px' }),
                      indicatorsContainer: (base: any) => ({ ...base, height: '40px' }),
                      singleValue: (base: any) => ({ ...base, fontSize: '0.9rem', fontFamily: 'var(--font-display)', fontWeight: 500 }),
                      placeholder: (base: any) => ({ ...base, fontSize: '0.9rem', fontFamily: 'var(--font-display)' }),
                      input: (base: any) => ({ ...base, fontFamily: 'var(--font-display)' }),
                    }}
                  />
                ) : (
                  <div className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#A0A4AE]" />
                    <p className="display-text-bold text-[14px] md:text-[15px] text-[#1A1C1E]">{displayState}</p>
                  </div>
                )}
              </div>
              )}

              {(isEditing || displaySchool) && (
              <div className="space-y-1 sm:col-span-2">
                <label className="display-text text-[10px] font-black uppercase tracking-[0.18em] text-[#74777F]/70">School</label>
                {isEditing ? (
                  <div className="relative" ref={schoolInputRef}>
                    <input
                      type="text"
                      value={school}
                      onChange={(e) => handleSchoolChange(e.target.value)}
                      onFocus={() => school.length >= 2 && debouncedSearch(school)}
                      className="display-text h-10 w-full rounded-[14px] border border-[#D8DDE7] bg-white px-3.5 text-sm text-[#1A1C1E] outline-none transition-colors focus:border-[#6541C4]"
                      placeholder="Enter school name"
                      autoComplete="off"
                    />
                    {showSchoolSuggestions && schoolSuggestions.length > 0 && (
                      <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-40 overflow-y-auto rounded-2xl border border-[#E5E7EB] bg-white shadow-xl">
                        {schoolSuggestions.map((s, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => handleSchoolSelect(s)}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-[#F8F9FE]"
                          >
                            <div className="font-semibold text-[#1A1C1E]">{s.school_name}</div>
                            {s.city && <div className="text-xs text-[#8A8F98]">{s.city}, {s.state}</div>}
                          </button>
                        ))}
                      </div>
                    )}
                    {fieldErrors.school && (
                      <p className="display-text pt-1 text-xs text-red-500">{fieldErrors.school}</p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <School className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#A0A4AE]" />
                    <p className="display-text-bold text-[14px] md:text-[15px] text-[#1A1C1E]">{displaySchool}</p>
                  </div>
                )}
              </div>
              )}

              {props.mode === 'parent' ? (
                <div className="sm:col-span-2 pt-1">
                  <ParentSubjectSection
                    freeSubject={(props as ParentModeProps).profileData.freeSubject ?? null}
                  />
                </div>
              ) : null}
            </div>

            {saveError && (
              <p className="display-text px-1 pt-4 text-sm text-red-600">{saveError}</p>
            )}

            <div className="mt-5 md:mt-6">
              {!isEditing ? (
                <div className="relative flex items-center justify-center">
                  <button
                    type="button"
                    onClick={handleStartEdit}
                    className={`${PRIMARY_BUTTON_CLASS} min-w-[190px] px-6 md:min-w-[220px]`}
                  >
                    <span className="text-[14px] md:text-[15px]">Edit Profile</span>
                  </button>
                  {footerTrailingAction ? (
                    <div className="absolute right-0 top-1/2 -translate-y-1/2">
                      {footerTrailingAction}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="flex gap-3">
                  <button
                    type="button"
                    className={`${SECONDARY_BUTTON_CLASS} flex-1`}
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                  >
                    <span className="text-sm">Cancel</span>
                  </button>
                  <button
                    type="button"
                    className={`${PRIMARY_BUTTON_CLASS} flex-1`}
                    onClick={handleSave}
                    disabled={!isFormValid || isSaving}
                  >
                    <span className="text-sm">{isSaving ? 'Saving…' : 'Save'}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
